import { Suspense, lazy, type ReactNode } from "react";
import { Box, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DashboardPanel from "@/components/ProjectPage/LeftToolsPanel/Panels/DashboardPanel";
import BreakdownPanel from "@/components/ProjectPage/LeftToolsPanel/Panels/BreakdownPanel";
import PropsPanel from "@/components/ProjectPage/LeftToolsPanel/Panels/PropsPanel";
import DialoguePanel from "@/components/ProjectPage/LeftToolsPanel/Panels/DialoguePanel";
import MusicSfxPanel from "@/components/ProjectPage/LeftToolsPanel/Panels/MusicSfxPanel";
import TimelinePanel from "@/components/ProjectPage/LeftToolsPanel/Panels/TimelinePanel";
import { observer } from "mobx-react-lite";
import { ProjectPanelRoute } from "@/types/routes";
import Loading from "@/pages/Loading";
import { SIDE_PANEL_COLLAPSE_TRANSITION } from "@/utils/constants";
import { useStore } from "@/contexts/StoreContext";
import { InlineHelpTooltipIcon } from "@/components/ui/GenerationModelHelpIcon";
import { projectHasAssignedStyleDna } from "@/utils/styleDna";

const STYLE_DNA_CREATOR_HELP_TEXT =
  "Test your style before applying it. Generate a sample, edit the style text if needed and test again. When you're happy, select the generated image to set the project's style.";

// Importing these directly (not via the Panels barrel) and via lazy(): the barrel's index.ts
// statically re-exports every panel, so importing anything from it - even unrelated named exports -
// pulls all of them into this chunk and defeats the split (confirmed by Vite's
// INEFFECTIVE_DYNAMIC_IMPORT warning when these went through the barrel).
const CharactersPanel = lazy(() => import("@/components/ProjectPage/LeftToolsPanel/Panels/CharactersPanel"));
const LocationsPanel = lazy(() => import("@/components/ProjectPage/LeftToolsPanel/Panels/LocationsPanel"));
const FramePanel = lazy(() => import("@/components/ProjectPage/LeftToolsPanel/Panels/FramePanel"));
const VideoPanel = lazy(() => import("@/components/ProjectPage/LeftToolsPanel/Panels/VideoPanel"));
const StyleDnaPanel = lazy(() => import("@/components/ProjectPage/LeftToolsPanel/Panels/StyleDnaPanel"));
const SystemSettingsPanel = lazy(() => import("@/components/ProjectPage/LeftToolsPanel/Panels/SystemSettingsPanel"));

interface LeftToolsPanelProps {
  readonly headerHeight: number;
  /** Column width — px number, CSS length, or MUI responsive width (breakpoint keys → media queries). */
  readonly width: number | string | Readonly<Record<string, string | number>>;
  readonly activeKey: string;
  readonly activeLabel: string;
  /** When false, animates closed (width → 0) so `MainSpace` expands into the freed space. */
  readonly open: boolean;
  readonly onClose: () => void;
}

const renderNavPanel = (activeKey: string) => {
  switch (activeKey) {
    case ProjectPanelRoute.Dashboard:
      return <DashboardPanel />;
    case ProjectPanelRoute.Breakdown:
      return <BreakdownPanel />;
    case ProjectPanelRoute.Characters:
      return <CharactersPanel />;
    case ProjectPanelRoute.Locations:
      return <LocationsPanel />;
    case ProjectPanelRoute.Props:
      return <PropsPanel />;
    case ProjectPanelRoute.ImageFrame:
      return <FramePanel />;
    case ProjectPanelRoute.Video:
      return <VideoPanel />;
    case ProjectPanelRoute.Dialogue:
      return <DialoguePanel />;
    case ProjectPanelRoute.Audio:
      return <MusicSfxPanel />;
    case ProjectPanelRoute.Timeline:
      return <TimelinePanel />;
    case ProjectPanelRoute.Settings:
      return <SystemSettingsPanel />;
    default:
      return <Typography sx={{ color: "text.secondary" }}>TODO: Settings/content for this section.</Typography>;
  }
};

const LeftToolsPanel = ({ headerHeight, width, activeKey, activeLabel, open, onClose }: LeftToolsPanelProps) => {
  const { actionStore } = useStore();
  const isStyleDnaTools = activeKey === ProjectPanelRoute.StyleDna;
  const { projectDetails } = actionStore;
  const hasAssignedStyleDna = projectHasAssignedStyleDna(projectDetails, projectDetails?.project_id ?? null);

  function resolveLeftToolsTitle(): string {
    if (isStyleDnaTools) {
      return "Style DNA creator";
    }

    if (activeKey === ProjectPanelRoute.Characters) {
      return "Character";
    }

    if (activeKey === ProjectPanelRoute.Locations) {
      return "Location";
    }

    return activeLabel;
  }

  const title = resolveLeftToolsTitle();

  function resolveLeftToolsContent(): ReactNode {
    // Panels (e.g. FramePanel) load their own data; don’t block mount on MobX projectDetails.
    return isStyleDnaTools ? <StyleDnaPanel /> : renderNavPanel(activeKey);
  }

  const content = resolveLeftToolsContent();

  return (
    <Paper
      elevation={0}
      sx={{
        position: "sticky",
        top: headerHeight,
        height: `calc(100vh - ${headerHeight}px)`,
        width: open ? width : 0,
        flexShrink: 0,
        borderRightWidth: open ? 1 : 0,
        borderRightStyle: "solid",
        borderRightColor: "divider",
        overflow: "hidden",
        transition: SIDE_PANEL_COLLAPSE_TRANSITION,
        bgcolor: "background.paper"
      }}
    >
      {/* Fixed to the fully-open width so content doesn't reflow while the Paper's width animates. */}
      <Box sx={{ width, height: "100%", display: "flex", flexDirection: "column" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", px: 2, pt: 3, pb: 3 }}>
          <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography sx={{ fontSize: "1rem", color: "text.primary", fontWeight: 500 }}>{title}</Typography>
            {isStyleDnaTools && hasAssignedStyleDna ? (
              <InlineHelpTooltipIcon title={STYLE_DNA_CREATOR_HELP_TEXT} ariaLabel="Style DNA creator help" color="common.white" />
            ) : null}
          </Box>
          <Tooltip title="Close" arrow>
            <IconButton size="small" onClick={onClose} sx={{ mt: -0.5, mr: -1 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
          <Box
            sx={{
              height: "100%",
              overflow: "auto",
              px: 2,
              pb: 2,
              display: "flex",
              flexDirection: "column",
              gap: 4,
              // While "Run Timeline" plays, the panel is visually dimmed (scrim below) and must also
              // be fully inert - no clicks, no drag-and-drop onto its reference dropzones, etc.
              pointerEvents: actionStore.isTimelineSequenceRunning ? "none" : "auto"
            }}
          >
            <Suspense fallback={<Loading fullScreen={false} />}>{content}</Suspense>
          </Box>
          <Box
            aria-hidden
            data-testid="left-tools-panel-dim-scrim"
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 1,
              pointerEvents: "none",
              bgcolor: "common.black",
              opacity: actionStore.isTimelineSequenceRunning ? 0.55 : 0,
              transition: "opacity 300ms ease"
            }}
          />
        </Box>
      </Box>
    </Paper>
  );
};

export default observer(LeftToolsPanel);
